// Game sounds, synthesised with Web Audio so there are no files to download.
// Browsers only allow audio after a user gesture, so the context is created
// (or resumed) on the first tap anywhere.
import { createPref } from "./prefs";

export const soundPref = createPref<"on" | "off">("dp.sound", () => "on", ["on", "off"]);

type Wave = OscillatorType;

let ctx: AudioContext | null = null;

function audio(): AudioContext | null {
  if (!ctx) {
    const AC = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AC) return null;
    ctx = new AC();
  }
  if (ctx.state === "suspended") void ctx.resume();
  return ctx;
}

/** Call once at startup: unlocks audio on the first tap or key press. */
export function unlockAudioOnGesture() {
  const unlock = () => {
    audio();
    window.removeEventListener("pointerdown", unlock);
    window.removeEventListener("keydown", unlock);
  };
  window.addEventListener("pointerdown", unlock);
  window.addEventListener("keydown", unlock);
}

interface Note {
  freq: number;
  at?: number;
  dur: number;
  wave?: Wave;
  gain?: number;
}

function play(notes: Note[]) {
  if (soundPref.get() === "off") return;
  const ac = audio();
  if (!ac || ac.state !== "running") return;
  const now = ac.currentTime;
  for (const n of notes) {
    const start = now + (n.at ?? 0);
    const osc = ac.createOscillator();
    const g = ac.createGain();
    osc.type = n.wave ?? "sine";
    osc.frequency.setValueAtTime(n.freq, start);
    const peak = n.gain ?? 0.18;
    g.gain.setValueAtTime(0.0001, start);
    g.gain.exponentialRampToValueAtTime(peak, start + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, start + n.dur);
    osc.connect(g).connect(ac.destination);
    osc.start(start);
    osc.stop(start + n.dur + 0.02);
  }
}

export const sfx = {
  /** Last ten seconds of a timer. */
  tick: () => play([{ freq: 1800, dur: 0.05, wave: "square", gain: 0.05 }]),
  /** One voting option or revealed answer appears. */
  ting: () => play([{ freq: 1568, dur: 0.35, gain: 0.14 }, { freq: 3136, dur: 0.2, gain: 0.04 }]),
  /** Your bluff or vote is in. */
  submit: () => play([{ freq: 660, dur: 0.08, wave: "triangle" }, { freq: 990, at: 0.07, dur: 0.12, wave: "triangle" }]),
  /** Everyone has submitted: voting begins. */
  allIn: () => play([523, 659, 784].map((freq, i) => ({ freq, at: i * 0.08, dur: 0.18, wave: "triangle" as Wave, gain: 0.12 }))),
  /** A new round starts. */
  round: () => play([{ freq: 392, dur: 0.15, wave: "triangle" }, { freq: 587, at: 0.12, dur: 0.25, wave: "triangle" }]),
  /** The real answer is revealed. */
  truth: () => play([523, 659, 784, 1047].map((freq) => ({ freq, dur: 0.6, wave: "triangle" as Wave, gain: 0.08 }))),
  /** Final screen. */
  win: () =>
    play([523, 659, 784, 1047, 784, 1047].map((freq, i) => ({ freq, at: i * 0.12, dur: i === 5 ? 0.7 : 0.16, wave: "triangle" as Wave, gain: 0.12 }))),
};
